import type { ListedPiece } from "@/lib/host-panel/catalog-types";
import type { BackendGiftListItemMutation, BackendGiftListProductItem } from "./backend-gift-list-schema";
import { decimalStringToCents } from "./decimal-string-to-cents";
import { itemTaxonomy, type ListCategory } from "./gift-list-item-taxonomy";

/**
 * `GiftListProductItemOutput` → `ListedPiece` (Requirement 5.1 to 5.3a).
 *
 * What the backend selects arrives intact: product UUID, name, store line, price (decimal →
 * cents), photo address, pack and purchases. What the response does not carry arrives **empty by
 * name**, never invented (Gate_Externo): `tone` and `sku` (`docs/backend-requests.md`, item 12),
 * `unit` (the backend knows no unit word) and `description`.
 *
 * **`category`, `type` and `material` are real since the backend sends `categoryId` and
 * `attributes` per product** (`feature/atributos-no-item-da-lista`). The category's name is joined
 * from the list's own `filters.categories` by id; the other two are read from the attributes by
 * name. Until that change is deployed — staging, at the time of writing — the response carries
 * none of it and all three stay empty, which is exactly what this mapper did before.
 *
 * **`featured` is real since 2026-10-09.** It was local and unpersisted until then: the star
 * moved a `useState`, the write diff did not compare it, and a re-read answered `false` — so the
 * host marked pieces, came back, and found none marked (reported 2026-10-08). The backend added
 * `GiftListItem.isFeatured`, per list, and this now reads it.
 */
export function fromBackendGiftListItem(
  item: BackendGiftListProductItem,
  /**
   * The list's `filters.categories`, which names the category the item points at by id. Optional
   * with an empty default: a caller that does not hold the aggregate (the catalog's join, a
   * response from before the backend sent the taxonomy) reads the category as empty, as before.
   */
  categories: readonly ListCategory[] = [],
): ListedPiece {
  const pack = item.isBundle ? item.bundleMultiple : 1;
  const taxonomy = itemTaxonomy(item.product, categories);

  return {
    id: item.product.id,
    name: item.product.name,
    // `GiftListProductSummaryOutput` (PRODUCT_SELECT) does not select the SKU, so the list's own
    // pieces are searchable by name only. Empty, by name — never guessed. Asked of the backend in
    // `docs/backend-requests.md`.
    sku: "",
    collection: item.product.collection?.name ?? "",
    priceCents: decimalStringToCents(item.product.price),
    /*
     * The pack the host chose, and only that: `isBundle`/`bundleMultiple` is a list decision.
     * The kit travels beside it, in its own fields, because the product being a set and the
     * host wanting groups are different facts (`architecture-standards.md` § "Kit e pacote são
     * coisas diferentes").
     */
    pack,
    isKit: item.product.isKit,
    /*
     * The list's item output does not carry the kit's component count — it says only whether
     * the product **is** a kit. Null, by name, rather than guessed from the pack: the pack is
     * the host's arrangement and has nothing to do with how many components the set holds.
     */
    componentCount: null,
    // A kit arranged in a pack is already counted by the pack; on its own it is still not "1
    // peça" — it is one kit, which is what the card then reads (`packLabel`).
    unit: item.product.isKit && !item.isBundle ? "kit" : "peça",
    // Empty, by name, whenever the response does not carry them (`gift-list-item-taxonomy.ts`).
    category: taxonomy.category,
    type: taxonomy.type,
    material: taxonomy.material,
    tone: "",
    // Already an address; `resolvePieceImageUrl` accepts both a key and an address (8.4).
    imageKey: item.product.imageUrl,
    description: "",
    // By construction: only items already on the list come through this output.
    inList: true,
    // Per list, not per product: the same piece can be featured in one list and not in another.
    featured: item.isFeatured ?? false,
    total: item.quantity * pack,
    purchased: item.quantityPurchased,
  };
}

/**
 * `ListedPiece` → one `UpsertGiftListItemDto` (Requirement 5.4): the arrangement the host
 * chose, in the backend's vocabulary — `quantity` counts packs when `isBundle`, pieces
 * otherwise, so `total = quantity × pack` on both sides.
 *
 * Two things the backend cannot represent are refused here rather than rounded away: a pack
 * that does not divide the total and an odd pack — `bundleMultiple` must be even
 * (`IsDivisibleBy(2)`).
 *
 * The pack dialog no longer produces either (`packOptions` offers "sem pacote" plus the even
 * divisors only), so this guard is no longer the dialog's backstop. It still earns its place:
 * a kit's `componentCount` becomes the piece's `pack` (`catalog-product.ts`), so a three-piece
 * kit added straight from the grid arrives here odd, with no dialog in the way. Refused by name
 * so the mismatch is a visible failure, not a silently different arrangement.
 *
 * Registered with the backend team, together with the measurement that the catalog held **no
 * kit at all** when this was written (2026-10-08).
 */
export function toBackendGiftListItemUpsert(
  piece: Pick<ListedPiece, "id" | "total" | "pack" | "featured">,
): BackendGiftListItemMutation {
  if (piece.pack < 1 || piece.total % piece.pack !== 0) {
    throw new RangeError(`toBackendGiftListItemUpsert: a pack of ${piece.pack} does not divide ${piece.total}.`);
  }

  if (piece.pack === 1) {
    return { productId: piece.id, quantity: piece.total, isBundle: false, isFeatured: piece.featured };
  }

  if (piece.pack % 2 !== 0) {
    throw new RangeError(
      `toBackendGiftListItemUpsert: the backend only accepts even packs (bundleMultiple), got ${piece.pack}.`,
    );
  }

  return {
    productId: piece.id,
    quantity: piece.total / piece.pack,
    isBundle: true,
    bundleMultiple: piece.pack,
    isFeatured: piece.featured,
  };
}

/** `ListedPiece` → the removal mutation. Removing a piece with purchases is the backend's 409 to raise. */
export function toBackendGiftListItemRemoval(piece: Pick<ListedPiece, "id">): BackendGiftListItemMutation {
  return { productId: piece.id, remove: true };
}
